/**
 * Widget V2 - REST API Client
 *
 * HTTP client for communicating with apointa-api REST endpoints.
 * Handles authentication, token refresh, and error handling.
 */

import { track } from "/imports/libs/analytics";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3003";

/**
 * Storage keys for tokens
 */
export const TOKEN_KEY = "widget_v2_token";
const REFRESH_TOKEN_KEY = "widget_v2_refresh_token";
const TOKEN_EXPIRY_KEY = "widget_v2_token_expiry";
const ACTIVE_CLIENT_KEY = "widget_v2_active_client";

/**
 * Get stored auth token
 */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Get stored refresh token
 */
export function getRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Store auth tokens
 */
export function setTokens({ token, refreshToken, expiresAt }) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  if (expiresAt) localStorage.setItem(TOKEN_EXPIRY_KEY, expiresAt.toString());
}

/**
 * Clear stored tokens
 */
export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem(ACTIVE_CLIENT_KEY);
}

/**
 * The family member the user is currently acting as. Persisted per-device so a
 * page reload keeps the selection. When unset (or equal to the head), the API
 * resolves the head by default. Cleared on logout so a stale id can't leak into
 * the next session.
 */
export function getActiveClientId() {
  return localStorage.getItem(ACTIVE_CLIENT_KEY);
}

export function setActiveClientId(clientId) {
  if (clientId) localStorage.setItem(ACTIVE_CLIENT_KEY, clientId);
  else localStorage.removeItem(ACTIVE_CLIENT_KEY);
}

// Append the active client id to a query string for user-scoped GETs.
function withActiveClient(query) {
  const clientId = getActiveClientId();
  if (clientId) query.set("clientId", clientId);
  return query;
}

// Merge the active client id into a request body for user-scoped POSTs.
function bodyWithActiveClient(body = {}) {
  const clientId = getActiveClientId();
  return clientId ? { ...body, clientId } : body;
}

/**
 * Check if token is expired or about to expire (within 1 minute)
 */
export function isTokenExpired() {
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiry) return true;

  // The API sends expiresAt as an ISO date; parseInt on it read "2026-…" as 2026 ms,
  // so every token looked expired and every request went through a refresh first.
  const expiryTime = /^\d+$/.test(expiry) ? parseInt(expiry, 10) : Date.parse(expiry);
  if (Number.isNaN(expiryTime)) return true;
  const now = Date.now();
  const bufferTime = 60 * 1000; // 1 minute buffer

  return now >= expiryTime - bufferTime;
}

/**
 * Refresh access token using refresh token.
 *
 * Concurrent callers share one in-flight request — without this, several API
 * calls firing while the token is expired would each POST /auth/refresh in
 * parallel and, if the server rotates refresh tokens, all but one would fail
 * and log the user out.
 */
let inflightRefresh = null;

async function refreshAccessToken() {
  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await fetch(`${API_URL}/api/v2/auth/refresh`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ refreshToken })
    });

    // Only a rejected refresh token ends the session. A 5xx or a deploy restart
    // must not sign the user out; the next request simply tries again.
    if (!response.ok) {
      if (response.status === 400 || response.status === 401) clearTokens();
      throw new Error("Token refresh failed");
    }

    const data = await response.json();
    setTokens({
      token: data.token,
      refreshToken: data.refreshToken,
      expiresAt: data.expiresAt
    });

    return data.token;
  })();

  try {
    return await inflightRefresh;
  } finally {
    inflightRefresh = null;
  }
}

/**
 * Get valid access token, refreshing if necessary
 */
async function getValidToken() {
  const token = getToken();
  if (!token) return null;

  if (isTokenExpired()) {
    try {
      return await refreshAccessToken();
    } catch (err) {
      console.error("Token refresh failed:", err);
      // A network blip keeps the stored token; the server decides if it is still good.
      return getToken();
    }
  }

  return token;
}

/**
 * Make an API request
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {Object} options - Fetch options
 * @param {boolean} options.auth - Whether to include auth token (default: true if logged in)
 */
async function apiRequest(endpoint, options = {}) {
  const { auth = true, retried = false, ...fetchOptions } = options;

  const headers = {
    "Content-Type": "application/json",
    ...fetchOptions.headers
  };

  // Add auth token if available and requested
  if (auth) {
    const token = await getValidToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const url = `${API_URL}${endpoint}`;

  const response = await fetch(url, {
    ...fetchOptions,
    headers
  });

  // An access token the server rejects gets one refresh-and-retry before the
  // caller sees the 401, so a stale access token never costs the session.
  if (response.status === 401 && auth && !retried && headers.Authorization && getRefreshToken()) {
    try {
      await refreshAccessToken();
      return apiRequest(endpoint, { ...options, retried: true });
    } catch (err) {
      // fall through to the original 401
    }
  }

  // Parse response
  let data;
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  // Handle errors
  if (!response.ok) {
    const error = new Error(data?.message || `Request failed: ${response.status}`);
    error.status = response.status;
    error.code = data?.error;
    error.data = data;
    throw error;
  }

  return data;
}

/**
 * Mutations are tracked here, once they succeed, so every screen that calls
 * them is covered.
 */
function trackOnSuccess(promise, event, props = {}) {
  return promise.then((res) => {
    track(event, props);
    return res;
  });
}

// A plan paid online only completes on Telr (plan_purchased fires on the result
// page); one settled in-app — wallet, voucher, free — is complete already.
function trackPlanPurchase(promise, planType, ids) {
  return promise.then((res) => {
    const props = { ...ids, plan_type: planType };
    track("plan_checkout_started", { ...props, next: res?.chargeId ? "online_payment" : "complete" });
    if (!res?.chargeId) track("plan_purchased", { ...props, invoice_id: res?.invoiceId || null, paid_online: false });
    return res;
  });
}

/**
 * API client with methods for all endpoints
 */
const api = {
  // ============ Widget ============

  /**
   * Initialize widget - get full configuration
   */
  initWidget: (slug, locationId = null) =>
    apiRequest(`/api/v2/widget/${slug}/init${locationId ? `?locationId=${locationId}` : ""}`, {
      auth: true
    }),

  /**
   * Get widget config only (lightweight)
   */
  getWidgetConfig: (slug) => apiRequest(`/api/v2/widget/${slug}/config`, { auth: false }),

  // ============ Availability ============

  /**
   * Get court availability
   */
  getCourtAvailability: (slug, params = {}) => {
    const query = new URLSearchParams();
    if (params.date) query.set("date", params.date);
    if (params.serviceId) query.set("serviceId", params.serviceId);
    if (params.facilityId) query.set("facilityId", params.facilityId);
    if (params.duration) query.set("duration", params.duration);
    if (params.locationId) query.set("locationId", params.locationId);

    return apiRequest(`/api/v2/widget/${slug}/courts/availability?${query.toString()}`);
  },

  /**
   * Get lesson availability
   */
  getLessonAvailability: (slug, params = {}) => {
    const query = new URLSearchParams();
    if (params.date) query.set("date", params.date);
    if (params.serviceId) query.set("serviceId", params.serviceId);
    if (params.trainerId) query.set("trainerId", params.trainerId);
    if (params.duration) query.set("duration", params.duration);
    if (params.locationId) query.set("locationId", params.locationId);
    if (params.facilityId) query.set("facilityId", params.facilityId);
    // Asks availability to answer the pack nudge for the slots it returns, so
    // the hint and the times arrive together.
    if (params.withOffers) query.set("withOffers", "1");
    // Composing the sessions an upfront pack commits to: once a first session
    // is picked, the day reaches to the pack's deadline rather than the club's
    // public booking window. The server re-resolves the variant before it widens.
    if (params.packProduct) query.set("packProduct", params.packProduct);
    if (params.packVariant) query.set("packVariant", params.packVariant);
    if (params.packFirstDate) query.set("packFirstDate", params.packFirstDate);

    return apiRequest(`/api/v2/widget/${slug}/lessons/availability?${withActiveClient(query).toString()}`);
  },

  // ============ Classes ============

  /**
   * Get class timetable
   */
  getClassTimetable: (slug, params = {}) => {
    const query = new URLSearchParams();
    if (params.dateStart) query.set("dateStart", params.dateStart);
    if (params.dateEnd) query.set("dateEnd", params.dateEnd);
    if (params.week) query.set("week", params.week);
    if (params.sport) query.set("sport", params.sport);
    if (params.age) query.set("age", params.age);
    if (params.locationId) query.set("locationId", params.locationId);
    if (params.trainerId) query.set("trainerId", params.trainerId);
    // Single-service view — the package upfront-booking picker lists only the
    // occurrences the pack being bought can cover.
    if (params.serviceId) query.set("serviceId", params.serviceId);
    // Standalone class categories (Events…): their own page asks for one
    // category, the Group Classes timetable leaves them out (comma-separated ids).
    if (params.categoryId) query.set("categoryId", params.categoryId);
    if (params.excludeCategoryIds) query.set("excludeCategoryIds", params.excludeCategoryIds);
    // Asks the timetable to answer the pack nudge for the sessions it returns,
    // so the cards and their hints arrive together.
    if (params.withOffers) query.set("withOffers", "1");
    // Picking the sessions an upfront pack commits to: the first pick lets the
    // timetable reach as far as the pack is valid, past the club's public
    // booking window. Re-resolved server-side from the product.
    if (params.packProduct) query.set("packProduct", params.packProduct);
    if (params.packVariant) query.set("packVariant", params.packVariant);
    if (params.packFirstDate) query.set("packFirstDate", params.packFirstDate);

    return apiRequest(`/api/v2/widget/${slug}/classes/timetable?${withActiveClient(query).toString()}`);
  },

  /**
   * Get single class details
   */
  getClassDetail: (slug, classId) => apiRequest(`/api/v2/widget/${slug}/classes/${classId}?${withActiveClient(new URLSearchParams()).toString()}`),

  /**
   * Class waitlist — join / leave a full session's list as the acting client,
   * and the client's own lists for the dashboard.
   */
  joinClassWaitlist: (slug, classId) =>
    trackOnSuccess(
      apiRequest(`/api/v2/widget/${slug}/classes/${classId}/waitlist`, { method: "POST", body: JSON.stringify(bodyWithActiveClient({})) }),
      "class_waitlist_joined",
      { class_id: classId }
    ),
  leaveClassWaitlist: (slug, classId) =>
    apiRequest(`/api/v2/widget/${slug}/classes/${classId}/waitlist?${withActiveClient(new URLSearchParams()).toString()}`, { method: "DELETE" }),
  getMyWaitlists: (slug) => apiRequest(`/api/v2/widget/${slug}/user/waitlists?${withActiveClient(new URLSearchParams()).toString()}`),

  // ============ Terms ============

  /**
   * Get terms list
   */
  getTerms: (slug, params = {}) => {
    const query = new URLSearchParams();
    if (params.sport) query.set("sport", params.sport);
    if (params.locationId) query.set("locationId", params.locationId);
    if (params.status) query.set("status", params.status);

    return apiRequest(`/api/v2/widget/${slug}/terms?${query.toString()}`);
  },

  /**
   * Get single term details
   */
  getTermDetail: (slug, termId) => apiRequest(`/api/v2/widget/${slug}/terms/${termId}`),

  // ============ Assessment ============

  /**
   * Get assessment configuration
   */
  getAssessmentConfig: (slug, sportId) => apiRequest(`/api/v2/widget/${slug}/assessment/${sportId}/config`),

  /**
   * Submit assessment form
   */
  submitAssessment: (slug, sportId, formData) =>
    apiRequest(`/api/v2/widget/${slug}/assessment/${sportId}/form`, {
      method: "POST",
      body: JSON.stringify({ formData })
    }),

  /**
   * Get assessment booking availability
   */
  getAssessmentAvailability: (slug, sportId, date) => apiRequest(`/api/v2/widget/${slug}/assessment/${sportId}/availability?date=${date}`),

  // ============ Bookings ============

  /**
   * Create a booking
   */
  createBooking: (slug, bookingData) =>
    apiRequest(`/api/v2/widget/${slug}/bookings`, {
      method: "POST",
      body: JSON.stringify(bodyWithActiveClient(bookingData))
    }),

  /**
   * Get booking details
   */
  getBooking: (slug, bookingId) => apiRequest(`/api/v2/widget/${slug}/bookings/${bookingId}`),

  /**
   * Resolve a shared booking link into a checkout summary + createBooking-ready
   * payload. Requires auth; scoped to the active family member.
   */
  getBookingJoinInfo: (slug, bookishId) =>
    apiRequest(`/api/v2/widget/${slug}/bookings/${bookishId}/join-info?${withActiveClient(new URLSearchParams()).toString()}`),

  // ============ User ============

  /**
   * Get user dashboard data
   */
  getUserDashboard: (slug) => apiRequest(`/api/v2/widget/${slug}/user/dashboard?${withActiveClient(new URLSearchParams()).toString()}`),

  /**
   * Get user activity (bookings, terms, packages, invoices)
   */
  getUserActivity: (slug, params = {}) => {
    const query = new URLSearchParams();
    if (params.tab) query.set("tab", params.tab);
    if (params.page) query.set("page", params.page);
    if (params.limit) query.set("limit", params.limit);
    if (params.scope) query.set("scope", params.scope);
    if (params.status) query.set("status", params.status);
    if (params.type) query.set("type", params.type);
    if (params.sort) query.set("sort", params.sort);
    withActiveClient(query);

    return apiRequest(`/api/v2/widget/${slug}/user/activity?${query.toString()}`);
  },

  /**
   * Get the client's sport-level details (richer than dashboard chips)
   */
  getUserSports: (slug) => apiRequest(`/api/v2/widget/${slug}/user/sports?${withActiveClient(new URLSearchParams()).toString()}`),

  /**
   * Profiles the logged-in user can act as (self + family dependents).
   */
  getFamily: (slug) => apiRequest(`/api/v2/widget/${slug}/user/family`),

  /**
   * The active client's saved preferences + birthDate, for the dashboard
   * Preferences tab.
   */
  getUserPreferences: (slug) => apiRequest(`/api/v2/widget/${slug}/user/preferences?${withActiveClient(new URLSearchParams()).toString()}`),

  /**
   * Update the active client's preferences from the dashboard.
   */
  updateUserPreferences: (slug, payload) =>
    apiRequest(`/api/v2/widget/${slug}/user/preferences`, {
      method: "PUT",
      body: JSON.stringify(bodyWithActiveClient(payload))
    }),

  /**
   * Set the active client's profile photo. `dataUrl` is a base64 image the
   * caller has already resized. Returns { clientId, imgId, imgUrl }.
   */
  uploadAvatar: (slug, dataUrl) =>
    apiRequest(`/api/v2/widget/${slug}/user/avatar`, {
      method: "POST",
      body: JSON.stringify(bodyWithActiveClient({ image: dataUrl }))
    }),

  /**
   * Confirm the account holder's own mobile number. Accepted only while the
   * stored number is missing or unusable — never sends the active client, since
   * a dependent's number stays the club's to manage.
   */
  confirmPhone: (slug, phone) =>
    apiRequest(`/api/v2/widget/${slug}/user/phone`, {
      method: "PUT",
      body: JSON.stringify({ phone })
    }),

  /**
   * Clear the active client's profile photo.
   */
  removeAvatar: (slug) =>
    apiRequest(`/api/v2/widget/${slug}/user/avatar`, {
      method: "DELETE",
      body: JSON.stringify(bodyWithActiveClient({}))
    }),

  /**
   * Personalized slot feed for the dashboard, stratified into court / private /
   * class, computed from the active client's saved preferences. Returns
   * { court, private, class, meta }.
   */
  getUserRecommendations: (slug) => apiRequest(`/api/v2/widget/${slug}/user/recommendations?${withActiveClient(new URLSearchParams()).toString()}`),

  /**
   * Packages owned by the client that have a session redeemable for the given
   * (service, location). Returns [] when no eligible package exists.
   */
  getRedeemablePackages: (slug, { serviceId, locationId, categoryId, pricingId, duration, date } = {}) => {
    const query = new URLSearchParams();
    if (serviceId) query.set("serviceId", serviceId);
    if (locationId) query.set("locationId", locationId);
    if (categoryId) query.set("categoryId", categoryId);
    // Session length — a pack sold for 30 minutes can't pay for an hour.
    if (pricingId) query.set("pricingId", pricingId);
    if (duration) query.set("duration", duration);
    // Session date — a pack expiring before the session can't pay for it.
    if (date) query.set("date", date);
    withActiveClient(query);
    return apiRequest(`/api/v2/widget/${slug}/user/redeemable-packages?${query.toString()}`);
  },

  /**
   * Vouchers (gift cards / wallet credit) owned by the client with balance > 0
   * and valid at the given location. `forSale` keeps only spend-anywhere wallets,
   * the only kind that can pay for a package.
   */
  getRedeemableVouchers: (slug, { locationId, forSale } = {}) => {
    const query = new URLSearchParams();
    if (locationId) query.set("locationId", locationId);
    if (forSale) query.set("forSale", "1");
    withActiveClient(query);
    return apiRequest(`/api/v2/widget/${slug}/user/redeemable-vouchers?${query.toString()}`);
  },

  /**
   * Whether the client has an active membership that grants member pricing for
   * the given (service, location). Returns { membership: null } when none.
   */
  getActiveMembership: (slug, { serviceId, locationId } = {}) => {
    const query = new URLSearchParams();
    if (serviceId) query.set("serviceId", serviceId);
    if (locationId) query.set("locationId", locationId);
    withActiveClient(query);
    return apiRequest(`/api/v2/widget/${slug}/user/active-membership?${query.toString()}`);
  },

  // ============ Purchase (catalog + buy) ============

  /**
   * List packages available for purchase from this widget's saas.
   */
  listPackagesForSale: (slug) => apiRequest(`/api/v2/widget/${slug}/purchase/packages`, { auth: false }),

  /**
   * Where the authenticated client's member pricing reaches on package
   * purchases: { hasMembership, memberEverywhere, memberLocationIds }. Read
   * through libs/memberPricing, which applies it the way the server does.
   */
  getPurchaseMembershipStatus: (slug) =>
    apiRequest(`/api/v2/widget/${slug}/purchase/me/membership-status?${withActiveClient(new URLSearchParams()).toString()}`),

  /**
   * List memberships available for purchase.
   */
  listMembershipsForSale: (slug) => apiRequest(`/api/v2/widget/${slug}/purchase/memberships`, { auth: false }),

  /**
   * List vouchers available for purchase.
   */
  listVouchersForSale: (slug) => apiRequest(`/api/v2/widget/${slug}/purchase/vouchers`, { auth: false }),

  /**
   * Sessions the club already booked for this client and never charged for,
   * that this pack is allowed to pay for. Returns { items: [...] }; an empty
   * list is the normal answer.
   */
  listClaimableSessions: (slug, { productId, pricingId, variantKey, sessions, sessionDuration, linkedServiceId, locationId } = {}) => {
    const query = new URLSearchParams();
    query.set("productId", productId || "");
    if (pricingId) query.set("pricingId", pricingId);
    if (variantKey) query.set("variantKey", variantKey);
    if (sessions != null) query.set("sessions", String(sessions));
    if (sessionDuration != null) query.set("sessionDuration", String(sessionDuration));
    if (linkedServiceId) query.set("linkedServiceId", linkedServiceId);
    if (locationId) query.set("locationId", locationId);
    return apiRequest(`/api/v2/widget/${slug}/purchase/packages/claimable-sessions?${withActiveClient(query).toString()}`);
  },

  /**
   * The pack series this client has booked ahead, and the packs on sale that
   * would carry each one on from the day after its last session. Returns
   * { items: [{ linkedServiceId, linkedServiceName, linkedCategoryType,
   * sessionDuration, lastSessionDate, continueFrom, reachDate,
   * options: [{ productId, variantKey }] }] }; an empty list is the normal answer.
   */
  listPackContinuations: (slug) =>
    apiRequest(`/api/v2/widget/${slug}/purchase/packages/continuations?${withActiveClient(new URLSearchParams()).toString()}`),

  /**
   * Buy a package. Returns { invoiceId, chargeId, isPaid } — caller should
   * redirect to /checkout/telr/:chargeId if chargeId is present. A `voucherId`
   * wallet must cover the whole price; it comes back paid with no chargeId.
   */
  purchasePackage: (
    slug,
    {
      productId,
      pricingId,
      locationId,
      variantKey,
      sessions,
      sessionDuration,
      linkedServiceId,
      upfrontBookishIds,
      upfrontBcIds,
      upfrontSlots,
      voucherId
    } = {}
  ) =>
    trackPlanPurchase(
      apiRequest(`/api/v2/widget/${slug}/purchase/packages`, {
        method: "POST",
        body: JSON.stringify(
          bodyWithActiveClient({
            productId,
            pricingId,
            locationId,
            variantKey,
            sessions,
            sessionDuration,
            linkedServiceId,
            upfrontBookishIds,
            upfrontBcIds,
            upfrontSlots,
            voucherId: voucherId || undefined
          })
        )
      }),
      "package",
      {
        product_id: productId,
        pricing_id: pricingId || null,
        sessions: sessions || null,
        upfront: !!(upfrontBookishIds?.length || upfrontBcIds?.length || upfrontSlots?.length),
        paid_with_wallet: !!voucherId
      }
    ),

  /**
   * Buy a membership. Returns { invoiceId, chargeId, isPaid } — caller should
   * redirect to /checkout/telr/:chargeId if chargeId is present.
   */
  purchaseMembership: (slug, { productId, pricingId, locationId } = {}) =>
    trackPlanPurchase(
      apiRequest(`/api/v2/widget/${slug}/purchase/memberships`, {
        method: "POST",
        body: JSON.stringify(bodyWithActiveClient({ productId, pricingId, locationId }))
      }),
      "membership",
      { product_id: productId, pricing_id: pricingId || null }
    ),

  /**
   * Buy a voucher. Returns { invoiceId, chargeId, isPaid } — caller should
   * redirect to /checkout/telr/:chargeId if chargeId is present.
   */
  purchaseVoucher: (slug, { voucherId, pricingId, locationId } = {}) =>
    trackPlanPurchase(
      apiRequest(`/api/v2/widget/${slug}/purchase/vouchers`, {
        method: "POST",
        body: JSON.stringify(bodyWithActiveClient({ voucherId, pricingId, locationId }))
      }),
      "voucher",
      { product_id: voucherId, pricing_id: pricingId || null }
    ),

  /**
   * Cancellation policy preview before the user confirms
   */
  getBookingRefundPreview: (slug, bookingId) =>
    apiRequest(`/api/v2/widget/${slug}/user/bookings/${bookingId}/refund-preview?${withActiveClient(new URLSearchParams()).toString()}`),

  /**
   * Cancel a booking owned by the authenticated client. `refundMethod` is required
   * when the club enables both WALLET and ONLINE; the chooser dialog supplies it.
   */
  cancelBooking: (slug, bookingId, { refundMethod } = {}) =>
    trackOnSuccess(
      apiRequest(`/api/v2/widget/${slug}/user/bookings/${bookingId}/cancel`, {
        method: "POST",
        body: JSON.stringify(bodyWithActiveClient({ refundMethod }))
      }),
      "booking_cancelled",
      { booking_id: bookingId, refund_method: refundMethod || null }
    ),

  /**
   * Sessions a paid class booking can be moved to. Paged (`page`, 1-based); returns
   * { mode, range, pack, creditAmount, options, page, pages, total }.
   */
  getRescheduleOptions: (slug, bookingId, { page } = {}) => {
    const params = new URLSearchParams();
    if (page) params.set("page", String(page));
    return apiRequest(`/api/v2/widget/${slug}/user/bookings/${bookingId}/reschedule-options?${withActiveClient(params).toString()}`);
  },

  /**
   * The times a paid private lesson can move to on one day — the lessons-availability
   * shape, plus `reschedule` ({ mode, creditAmount, pack, locationIds, from }).
   */
  getLessonRescheduleSlots: (slug, bookingId, { date, trainerId, locationId } = {}) => {
    const params = new URLSearchParams();
    if (date) params.set("date", date);
    if (trainerId) params.set("trainerId", trainerId);
    if (locationId) params.set("locationId", locationId);
    return apiRequest(`/api/v2/widget/${slug}/user/bookings/${bookingId}/reschedule-lesson-slots?${withActiveClient(params).toString()}`);
  },

  /**
   * Up to a week of the times a paid private lesson can move to, in one call: `days`
   * (1–7) consecutive days from `from`. The one-day shape plus `days` ({ date: slots }).
   */
  getLessonRescheduleWeek: (slug, bookingId, { from, days, trainerId, locationId } = {}) => {
    const params = new URLSearchParams({ from, days: String(days) });
    if (trainerId) params.set("trainerId", trainerId);
    if (locationId) params.set("locationId", locationId);
    return apiRequest(`/api/v2/widget/${slug}/user/bookings/${bookingId}/reschedule-lesson-week?${withActiveClient(params).toString()}`);
  },

  /**
   * Up to a week of the times a paid court booking can move to, in one call: `days`
   * (1–7) consecutive days from `from`. The one-day shape plus `days` ({ date: slots }).
   */
  getCourtRescheduleWeek: (slug, bookingId, { from, days, locationId, facilityId } = {}) => {
    const params = new URLSearchParams({ from, days: String(days) });
    if (locationId) params.set("locationId", locationId);
    if (facilityId) params.set("facilityId", facilityId);
    return apiRequest(`/api/v2/widget/${slug}/user/bookings/${bookingId}/reschedule-court-week?${withActiveClient(params).toString()}`);
  },

  /**
   * The times a paid court booking can move to on one day — the courts-availability
   * shape, plus `reschedule` ({ mode, creditAmount, locationIds, from }).
   */
  getCourtRescheduleSlots: (slug, bookingId, { date, locationId, facilityId } = {}) => {
    const params = new URLSearchParams();
    if (date) params.set("date", date);
    if (locationId) params.set("locationId", locationId);
    if (facilityId) params.set("facilityId", facilityId);
    return apiRequest(`/api/v2/widget/${slug}/user/bookings/${bookingId}/reschedule-court-slots?${withActiveClient(params).toString()}`);
  },

  /**
   * Move a paid booking, paid from the same source (the package session, or the refunded
   * amount via the wallet). A class names the session (`targetBookingId`); a private
   * lesson or court names the new slot (`slot`: date, time, trainerId (lesson only),
   * facilityId, facilityAuto, locationId). Returns { bookingId, bookingClientId, invoiceId, mode, requiresPayment }.
   */
  rescheduleBooking: (slug, bookingId, { targetBookingId, slot } = {}) =>
    apiRequest(`/api/v2/widget/${slug}/user/bookings/${bookingId}/reschedule`, {
      method: "POST",
      body: JSON.stringify(bodyWithActiveClient({ targetBookingId, slot }))
    }),

  /**
   * Check in to a booking. One-way — there is no undo endpoint. Calling it a
   * second time succeeds and returns the original timestamp.
   */
  checkInBooking: (slug, bookingId) =>
    apiRequest(`/api/v2/widget/${slug}/user/bookings/${bookingId}/check-in`, {
      method: "POST",
      body: JSON.stringify(bodyWithActiveClient({}))
    }),

  // Add to calendar paths arrive signed from the server — the token in them is
  // the auth, so they also work on the post-payment screens, which have no session.
  calendarFileUrl: (path) => `${API_URL}${path}`,
  markCalendarAdded: (path) => apiRequest(path, { method: "POST", auth: false }),

  /**
   * Full invoice detail for the dashboard's view-invoice dialog
   */
  getInvoice: (slug, invoiceId) =>
    apiRequest(`/api/v2/widget/${slug}/user/invoices/${invoiceId}?${withActiveClient(new URLSearchParams()).toString()}`),

  /**
   * Resolve the chargeId for an unpaid invoice (then redirect to /checkout/telr/:chargeId)
   */
  getInvoicePayLink: (slug, invoiceId) =>
    apiRequest(`/api/v2/widget/${slug}/user/invoices/${invoiceId}/pay-link?${withActiveClient(new URLSearchParams()).toString()}`),

  /**
   * Session-by-session usage for one paid plan. Returns { plan, summary, usage }.
   */
  getPlanUsage: (slug, planId) =>
    apiRequest(`/api/v2/widget/${slug}/user/plans/${planId}/usage?${withActiveClient(new URLSearchParams()).toString()}`),

  /**
   * Mint the invoice for a booking the club created but never checked out, so it
   * can go through getInvoicePayLink. Returns { invoiceId, created }; a booking
   * that already has one returns it untouched.
   */
  createBookingInvoice: (slug, bookingId) =>
    apiRequest(`/api/v2/widget/${slug}/user/bookings/${bookingId}/invoice`, {
      method: "POST",
      body: JSON.stringify(bodyWithActiveClient({}))
    }),

  /**
   * Settle a scheduler-created booking the club never checked out, applying the
   * credit the client picked (package / voucher / neither). Returns
   * { invoiceId, isPaid }; when isPaid is false, resolve the chargeId via
   * getInvoicePayLink and redirect to /checkout/telr/:chargeId.
   */
  settleBooking: (slug, bookingId, { paidPlanId, voucherId } = {}) =>
    apiRequest(`/api/v2/widget/${slug}/user/bookings/${bookingId}/settle`, {
      method: "POST",
      body: JSON.stringify(bodyWithActiveClient({ paidPlanId, voucherId }))
    }),

  // ============ Auth ============

  /**
   * Login with email/password
   */
  login: async (email, password, saasId = null) => {
    const data = await apiRequest("/api/v2/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, saasId }),
      auth: false
    });

    setTokens({
      token: data.token,
      refreshToken: data.refreshToken,
      expiresAt: data.expiresAt
    });
    track("logged_in", { method: "password" });

    return data;
  },

  /**
   * Register new user
   */
  register: async (userData) => {
    const data = await apiRequest("/api/v2/auth/register", {
      method: "POST",
      body: JSON.stringify(userData),
      auth: false
    });

    setTokens({
      token: data.token,
      refreshToken: data.refreshToken,
      expiresAt: data.expiresAt
    });
    track("signed_up", { method: "email", needs_verification: !data.token });

    return data;
  },

  /**
   * Sign in or sign up with a Google authorization code. The response `status`
   * says what happened: "signed_in", "needs_profile" (collect a phone, then
   * googleComplete) or "needs_onboard" (existing Apointa user joining this club).
   */
  googleAuth: async (code, slug) => {
    const data = await apiRequest("/api/v2/auth/google", {
      method: "POST",
      body: JSON.stringify({ code, slug }),
      auth: false
    });

    if (data.token) {
      setTokens({
        token: data.token,
        refreshToken: data.refreshToken,
        expiresAt: data.expiresAt
      });
    }
    // "needs_profile" is a first-time signup — counted once googleComplete finishes it.
    if (data.status !== "needs_profile") track("logged_in", { method: "google" });

    return data;
  },

  /**
   * Finish a first-time Google signup with the phone number Google can't provide.
   */
  googleComplete: async (profileToken, phone) => {
    const data = await apiRequest("/api/v2/auth/google/complete", {
      method: "POST",
      body: JSON.stringify({ profileToken, phone }),
      auth: false
    });

    setTokens({
      token: data.token,
      refreshToken: data.refreshToken,
      expiresAt: data.expiresAt
    });
    track("signed_up", { method: "google", needs_verification: false });

    return data;
  },

  /**
   * Look up a club access code and get the client's prefill data without
   * consuming it. Used before showing the claim signup form.
   */
  claimVerify: (code, slug) =>
    apiRequest("/api/v2/auth/claim/verify", {
      method: "POST",
      body: JSON.stringify({ code, slug }),
      auth: false
    }),

  /**
   * Consume a club access code: create an account for the existing client
   * record it resolves to, and link them.
   */
  claim: async ({ code, slug, email, phoneNumber, password }) => {
    const data = await apiRequest("/api/v2/auth/claim", {
      method: "POST",
      body: JSON.stringify({ code, slug, email, phoneNumber, password }),
      auth: false
    });

    setTokens({
      token: data.token,
      refreshToken: data.refreshToken,
      expiresAt: data.expiresAt
    });
    track("account_claimed", { method: "access_code" });

    return data;
  },

  /**
   * Look up a trainer access code and get the trainer's prefill data without
   * consuming it. Used before showing the set-password step.
   */
  trainerClaimVerify: (code, slug) =>
    apiRequest("/api/v2/auth/trainer/claim/verify", {
      method: "POST",
      body: JSON.stringify({ code, slug }),
      auth: false
    }),

  /**
   * Consume a trainer access code: set the password on the trainer's existing
   * account and log them in. No email verification — the club issued the code.
   */
  trainerClaim: async ({ code, slug, password }) => {
    const data = await apiRequest("/api/v2/auth/trainer/claim", {
      method: "POST",
      body: JSON.stringify({ code, slug, password }),
      auth: false
    });

    setTokens({
      token: data.token,
      refreshToken: data.refreshToken,
      expiresAt: data.expiresAt
    });

    return data;
  },

  /**
   * The signed-in trainer's read-only schedule. Defaults to today + tomorrow;
   * pass { scope: "upcoming"|"past"|"all" } or a { from, to } range, plus
   * { page, limit } for pagination, or { view: "week", from } for the whole
   * Mon–Sun week grid. Authed by token; the server resolves the trainer, so no
   * slug or id is passed.
   */
  getTrainerSchedule: ({ view, scope, from, to, page, limit } = {}) => {
    const query = new URLSearchParams();
    if (view) query.set("view", view);
    if (scope) query.set("scope", scope);
    if (from) query.set("from", from);
    if (to) query.set("to", to);
    if (page) query.set("page", page);
    if (limit) query.set("limit", limit);
    const qs = query.toString();
    return apiRequest(`/api/v2/trainer/schedule${qs ? `?${qs}` : ""}`);
  },

  /**
   * Mark one of the signed-in trainer's attendees as Attended / No Show / LMC /
   * TBC (or reset to NEW). The server resolves the trainer and verifies the
   * booking is theirs, so only the bookish-client id and new status are sent.
   */
  markTrainerAttendance: ({ bookishClientId, status }) =>
    apiRequest("/api/v2/trainer/attendance", {
      method: "POST",
      body: JSON.stringify({ bookishClientId, status })
    }),

  /**
   * Add or edit the signed-in trainer's own note on one of their bookings. One
   * note per trainer per booking, so a second save edits the first; there is no
   * delete. The server resolves the trainer and verifies the booking is theirs.
   */
  saveTrainerBookingNote: ({ bookishId, text }) =>
    apiRequest("/api/v2/trainer/booking-note", {
      method: "PUT",
      body: JSON.stringify({ bookishId, text })
    }),

  /**
   * Senior trainers only: the whole club's schedule for one location. Mirrors
   * the partners calendar modes — day view of every trainer/court, or a
   * Mon–Sun week of one specific entity. All params optional; the server
   * defaults to day / trainers / all / the trainer's own location / today.
   */
  getTrainerFullSchedule: ({ view, entityType, entityId, locationId, date } = {}) => {
    const query = new URLSearchParams();
    if (view) query.set("view", view);
    if (entityType) query.set("entityType", entityType);
    if (entityId) query.set("entityId", entityId);
    if (locationId) query.set("locationId", locationId);
    if (date) query.set("date", date);
    const qs = query.toString();
    return apiRequest(`/api/v2/trainer/full-schedule${qs ? `?${qs}` : ""}`);
  },

  /**
   * Set the per-client note (BookishClient.note). Same gate as attendance —
   * the trainer's own bookings, or any booking for a senior. Empty clears.
   */
  updateTrainerClientNote: ({ bookishClientId, note }) =>
    apiRequest("/api/v2/trainer/client-note", {
      method: "POST",
      body: JSON.stringify({ bookishClientId, note })
    }),

  /**
   * Existing-client email signup, step 1: ask the API to email a claim link if
   * this address matches an unclaimed client. Resolves with a `status` of
   * "link_sent", "already_registered", or "not_found".
   */
  signupLookup: (email, slug) =>
    apiRequest("/api/v2/auth/signup/lookup", {
      method: "POST",
      body: JSON.stringify({ email, slug }),
      auth: false
    }),

  /**
   * Validate an email-claim link token and get the client's prefill data.
   */
  claimEmailVerify: (token) =>
    apiRequest("/api/v2/auth/signup/claim-email/verify", {
      method: "POST",
      body: JSON.stringify({ token }),
      auth: false
    }),

  /**
   * Complete an email-based claim: set a password for the resolved client.
   * The link proved ownership, so this logs them in immediately.
   */
  claimEmail: async (token, password, phoneNumber) => {
    const data = await apiRequest("/api/v2/auth/signup/claim-email", {
      method: "POST",
      body: JSON.stringify({ token, password, phoneNumber }),
      auth: false
    });

    setTokens({
      token: data.token,
      refreshToken: data.refreshToken,
      expiresAt: data.expiresAt
    });
    track("account_claimed", { method: "email_link" });

    return data;
  },

  forgotPassword: (email, slug) =>
    apiRequest("/api/v2/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ email, slug }),
      auth: false
    }),

  resetPassword: (token, password) =>
    apiRequest("/api/v2/auth/reset-password", {
      method: "POST",
      body: JSON.stringify({ token, password }),
      auth: false
    }),

  /**
   * Verify an account's email from the link token, which also logs them in.
   */
  verifyEmail: async (token) => {
    const data = await apiRequest("/api/v2/auth/verify-email", {
      method: "POST",
      body: JSON.stringify({ token }),
      auth: false
    });

    setTokens({
      token: data.token,
      refreshToken: data.refreshToken,
      expiresAt: data.expiresAt
    });
    track("email_verified");

    return data;
  },

  /**
   * Logout - clear tokens
   */
  logout: () => {
    clearTokens();
  },

  /**
   * Get current user info
   */
  getCurrentUser: (slug) => apiRequest(`/api/v2/auth/me${slug ? `?slug=${encodeURIComponent(slug)}` : ""}`),

  /**
   * Join the current saas as a client (already-logged-in user)
   */
  onboard: (saasId) =>
    trackOnSuccess(
      apiRequest("/api/v2/auth/onboard", {
        method: "POST",
        body: JSON.stringify({ saasId })
      }),
      "club_joined"
    ),

  /**
   * Save a brand-new client's onboarding preferences. Authorized by the
   * short-lived preferences token from signup, so no session is required —
   * this runs before the client verifies their email.
   */
  savePreferences: (payload) =>
    apiRequest("/api/v2/auth/preferences", {
      method: "POST",
      body: JSON.stringify(payload),
      auth: false
    }),

  /**
   * Check if user is logged in
   */
  isLoggedIn: () => !!getToken(),

  // -------- Push notifications (this device) --------
  // The server works out whether the signed-in user is a client or a trainer at
  // the club; the widget only hands over the browser's subscription.

  pushSubscribe: (slug, subscription, platform) =>
    apiRequest(`/api/v2/widget/${slug}/push/subscriptions`, {
      method: "POST",
      body: JSON.stringify({ subscription, platform })
    }),

  // Removes this club's server row only. The browser subscription itself stays:
  // every academy installed from this origin shares it (one service worker).
  pushUnsubscribe: (slug, endpoint) =>
    apiRequest(`/api/v2/widget/${slug}/push/subscriptions`, {
      method: "DELETE",
      body: JSON.stringify({ endpoint })
    }),

  // { known } — false when the server has no live row for this device and user,
  // which the caller answers by subscribing again.
  pushTouch: (slug, endpoint) =>
    apiRequest(`/api/v2/widget/${slug}/push/subscriptions/touch`, {
      method: "POST",
      body: JSON.stringify({ endpoint })
    }),

  // Sends "It works" to the caller's devices. Dev/staging only (404 in production).
  pushTest: (slug) =>
    apiRequest(`/api/v2/widget/${slug}/push/test`, {
      method: "POST"
    }),

  // -------- Checkout (Telr) --------
  // No auth required — chargeId in the URL is the entitlement.

  getTelrCharge: (chargeId) => apiRequest(`/api/v2/checkout/telr/${chargeId}`, { auth: false }),

  getBookingConfirmation: (invoiceId) => apiRequest(`/api/v2/checkout/confirmation/${invoiceId}`, { auth: false }),

  initiateTelrPayment: (chargeId, paymentData, clubId) =>
    apiRequest(`/api/v2/checkout/telr/${chargeId}/initiate`, {
      method: "POST",
      auth: false,
      body: JSON.stringify({ paymentData, clubId })
    }),

  recordTelrTransaction: (chargeId, payId) =>
    apiRequest(`/api/v2/checkout/telr/${chargeId}/transaction`, {
      method: "POST",
      auth: false,
      body: JSON.stringify({ payId })
    }),

  checkTelrStatus: (chargeId) =>
    apiRequest(`/api/v2/checkout/telr/${chargeId}/check-status`, {
      method: "POST",
      auth: false
    }),

  retryTelrPayment: (chargeId) =>
    apiRequest(`/api/v2/checkout/telr/${chargeId}/retry`, {
      method: "POST",
      auth: false
    }),

  releaseCancelledBooking: (chargeId) =>
    apiRequest(`/api/v2/checkout/telr/${chargeId}/release-cancelled`, {
      method: "POST",
      auth: false
    }),

  // -------- Hold checkout (split payment / open games) --------
  // No auth required — the holdToken in the URL is the entitlement. Backed by
  // the open_games doc, not an invoice: the host authorizes a card HOLD for
  // the full court amount; money moves later at the deadline capture.

  getTelrHold: (holdToken) => apiRequest(`/api/v2/checkout/telr-hold/${holdToken}`, { auth: false }),

  initiateTelrHold: (holdToken, clubId) =>
    apiRequest(`/api/v2/checkout/telr-hold/${holdToken}/initiate`, {
      method: "POST",
      auth: false,
      body: JSON.stringify({ clubId })
    }),

  checkTelrHoldStatus: (holdToken) =>
    apiRequest(`/api/v2/checkout/telr-hold/${holdToken}/check-status`, {
      method: "POST",
      auth: false
    })
};

export default api;
